import { useMemo, useState } from 'react'
import { Modal } from './Modal'
import { formatWeekRange } from '../lib/dates'
import { DAY_NAMES, type Note, type Person, type Week } from '@shared/types'

interface Props {
  week: Week
  currentUser: string
  people: Person[]
  emailConfigured: boolean
  busy: boolean
  onSend: (payload: { message: string; editNoteId: string | null }) => void
  onClose: () => void
}

/**
 * The confirm step before a week goes out.
 *
 * Sending used to be one click with no way back and no way to say anything,
 * which is the wrong shape for the one action in this app that reaches another
 * person. This says who it reaches, shows what they will see, and lets you put
 * a sentence on the front of it.
 */
export function ReviewComposer({
  week,
  currentUser,
  people,
  emailConfigured,
  busy,
  onSend,
  onClose,
}: Props) {
  const me = currentUser.toLowerCase()
  const reviewers = people.filter((p) => p.notifyReview && p.name.toLowerCase() !== me)

  const weekNotes = useMemo(() => week.notes.filter((n) => n.dayOfWeek === null), [week.notes])
  /* Yours, most recent first: editing someone else's note in their name would
     be putting words in their mouth. */
  const mine = useMemo(
    () => [...weekNotes].reverse().find((n) => n.author.toLowerCase() === me) ?? null,
    [weekNotes, me],
  )

  const [mode, setMode] = useState<'new' | 'edit'>('new')
  const [message, setMessage] = useState('')

  const editing: Note | null = mode === 'edit' ? mine : null
  const value = editing ? (message === '' ? editing.body : message) : message

  const packedDays = week.lunches.filter(
    (l) => l.status === 'packing' && l.items.length > 0,
  ).length
  const offDays = week.lunches.filter((l) => l.status !== 'packing').length

  function send() {
    if (busy) return
    const text = value.trim()
    if (editing && !text) return // editing a note to nothing is a delete, not a send
    onSend({ message: text, editNoteId: editing ? editing.id : null })
  }

  return (
    <Modal
      title={`Send ${formatWeekRange(week.startDate)} for review`}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <div className="spacer" />
          <button
            className="btn primary"
            onClick={send}
            disabled={busy || (editing !== null && value.trim() === '')}
          >
            {busy
              ? 'Sending…'
              : reviewers.length > 0 && emailConfigured
                ? `Send to ${reviewers.map((p) => p.name).join(' and ')}`
                : 'Mark ready for review'}
          </button>
        </>
      }
    >
      <div className="field">
        <label>Goes to</label>
        {!emailConfigured ? (
          <p className="muted" style={{ fontSize: 13.5, margin: 0 }}>
            No email key is set on the Worker, so this will only change the week&rsquo;s state —
            nothing will be sent.
          </p>
        ) : reviewers.length === 0 ? (
          <p className="muted" style={{ fontSize: 13.5, margin: 0 }}>
            Nobody has review emails switched on, so this will only change the week&rsquo;s
            state. Add an address under the gear to have it actually send.
          </p>
        ) : (
          <div className="recipients">
            {reviewers.map((p) => (
              <span className="recipient" key={p.id}>
                <strong>{p.name}</strong> {p.email}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="field">
        <label>What they&rsquo;ll see</label>
        <p className="muted" style={{ fontSize: 13.5, margin: 0 }}>
          The whole week — {packedDays} day{packedDays === 1 ? '' : 's'} packed
          {offDays > 0 && `, ${offDays} off`} — every item on it, and{' '}
          {weekNotes.length === 0
            ? 'any note you add below'
            : `${weekNotes.length} week note${weekNotes.length === 1 ? '' : 's'}`}
          , with a link straight to this week.
        </p>
      </div>

      <div className="field">
        <label htmlFor="review-message">
          {editing ? 'Your note' : 'Add a note (optional)'}
        </label>
        {mine && (
          <div className="row wrap" style={{ marginBottom: 2 }}>
            <button
              className="btn sm"
              aria-pressed={mode === 'new'}
              onClick={() => {
                setMode('new')
                setMessage('')
              }}
              disabled={busy}
            >
              Add a new note
            </button>
            <button
              className="btn sm"
              aria-pressed={mode === 'edit'}
              onClick={() => {
                setMode('edit')
                setMessage('')
              }}
              disabled={busy}
            >
              Edit my last note
            </button>
          </div>
        )}
        <textarea
          id="review-message"
          className="note-input"
          value={value}
          maxLength={4000}
          placeholder={
            editing
              ? undefined
              : 'Why these days look like this, anything to watch for, anything you want checked…'
          }
          onChange={(e) => setMessage(e.target.value)}
          autoFocus
        />
        <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>
          {editing
            ? 'Replaces the note you already left on this week.'
            : 'Saved as a week note, so it stays on the plan as well as going in the email.'}
        </p>
      </div>

      {weekNotes.length > 0 && (
        <div className="field">
          <label>Notes already on this week</label>
          <div className="note-preview">
            {weekNotes.map((n) => (
              <div className={`note-preview-row ${editing?.id === n.id ? 'editing' : ''}`} key={n.id}>
                <span className="who">
                  {n.author || 'Someone'} · {n.createdAt.slice(0, 10)}
                </span>
                <span className="body">{editing?.id === n.id ? value : n.body}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {week.notes.some((n) => n.dayOfWeek !== null) && (
        <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>
          Day notes go too:{' '}
          {week.notes
            .filter((n) => n.dayOfWeek !== null)
            .map((n) => DAY_NAMES[n.dayOfWeek! - 1])
            .join(', ')}
          .
        </p>
      )}
    </Modal>
  )
}
